import { Alert, Badge, Button, ButtonGroup, Card, Col, Container, Form, Modal, Row, Table } from "react-bootstrap";
import { useTissueReportQuery } from "../../queries";
import { useCallback, useMemo, useRef, useState } from "react";
import { AutoCol, Checkbox, ControlledDateInput, Icon, LoadingSpinner } from "../../component-library";
import { TissueFollowUp, useSendTissueReports } from ".";
import { Claims, Person, SurgeonWithTissues, TissueReportSlugs } from "../../models";
import { useAxios, useStoredState } from "../../hooks";
import { usePermissions } from "../../contexts";
import { Link } from "react-router-dom";
import { fas } from "@fortawesome/free-solid-svg-icons";
import { getLastDayOfLastMonth, getLastMonthDay1 } from "../../helper-functions";

export function ThreeToSixMonthFollowUpReport() {
    const { post } = useAxios();
    const { hasPermission } = usePermissions();
    const [startDate, setStartDate] = useStoredState<string>("3-6-month-report-start", getLastMonthDay1());
    const [endDate, setEndDate] = useStoredState<string>("3-6-month-report-end", getLastDayOfLastMonth());
    const [includeSent, setIncludeSent] = useStoredState<boolean>("3-6-month-report-include-sent", false);
    const [activeSurgeonId, setActiveSurgeonId] = useState<number | null>(null);
    const [showModal, setShowModal] = useState(false);
    const surgeonRefs = useRef<(HTMLDivElement | null)[]>([]);

    const dateRange = useMemo(() => {
        if (!startDate || !endDate) return "";
        const start = new Date(startDate);
        const end = new Date(endDate);
        const startYear = start.getFullYear();
        const startMonth = start.getMonth() + 1;
        const startDay = start.getDate();
        const endYear = end.getFullYear();
        const endMonth = end.getMonth() + 1;
        const endDay = end.getDate();
        return `${startYear}-${startMonth}-${startDay} to ${endYear}-${endMonth}-${endDay}`;
    }, [startDate, endDate]);

    const invalidDates = useMemo(() => {
        const start = new Date(startDate);
        const end = new Date(endDate);
        return start > end;
    }, [startDate, endDate]);

    const {
        query: { data, isFetching },
    } = useTissueReportQuery({
        reportName: TissueReportSlugs.ThreeToSixMonthFollowUp,
        startDate,
        endDate,
        includeSent,
    });

    const getContactInfoForSurgeon = (surgeon: Person) => {
        if (
            surgeon.postopFormCommunicationType?.toLowerCase() === "email" &&
            !!surgeon.postopFormCommunication &&
            isNaN(Number(surgeon.postopFormCommunication))
        ) {
            return surgeon.postopFormCommunication;
        }
        if (surgeon.postopFormCommunicationType?.toLowerCase() === "fax" && surgeon.postopFormCommunication) {
            return surgeon.postopFormCommunication;
        }
        if (surgeon.assistantEmail) {
            return surgeon.assistantEmail;
        }
        if (surgeon.assistantFax) {
            return surgeon.assistantFax;
        }
        if (surgeon.email) {
            return surgeon.email;
        }
        if (surgeon.fax) {
            return surgeon.fax;
        }
        return null;
    };

    const surgeonHasContactInfo = (surgeon: Person) => {
        return !!getContactInfoForSurgeon(surgeon);
    };

    const allSurgeonsHaveContactInfo = data?.every((group) => surgeonHasContactInfo(group.surgeon));

    const getSurgeonVariant = (surgeon: Person) => {
        if (!surgeonHasContactInfo(surgeon)) {
            return "danger";
        }
        return activeSurgeonId === surgeon.id ? "primary" : "outline-primary";
    };

    const scrollToSection = (index: number) => {
        const element = surgeonRefs.current[index];
        if (element) {
            element.scrollIntoView();
        }
    };

    const handleSurgeonClick = (surgeonId: number, index: number) => {
        setActiveSurgeonId(surgeonId);
        scrollToSection(index);
    };

    const onUpload = useCallback(
        (data: SurgeonWithTissues, formData: FormData): Promise<void> => {
            const url = `/tissue-reports/${TissueReportSlugs.ThreeToSixMonthFollowUp}?surgeonId=${data.surgeon.id}&tissueIds=${data.tissues.map((tissue) => tissue.id).join(",")}`;
            return post<FormData, void>({
                url: encodeURI(url),
                data: formData,
            });
        },
        [post]
    );

    const { sendPdfs, isUploading } = useSendTissueReports({
        refs: surgeonRefs,
        data: data ?? [],
        dateRange,
        onUpload,
    });

    return (
        <Container>
            <h1>3-6 Month Post-Operative Follow-Up Report</h1>

            <Row className="mb-3">
                <AutoCol>
                    <ControlledDateInput
                        label="Start Date"
                        value={startDate}
                        onChange={(e) => setStartDate(e?.toISOString() ?? "")}
                    />
                </AutoCol>
                <AutoCol>
                    <ControlledDateInput
                        label="End Date"
                        value={endDate}
                        onChange={(e) => setEndDate(e?.toISOString() ?? "")}
                    />
                </AutoCol>
                <AutoCol className="d-flex align-items-center">
                    <Form.Check
                        type="checkbox"
                        id="include-sent"
                        label="Include tissues already sent"
                        checked={includeSent}
                        onChange={(e) => setIncludeSent(e.target.checked)}
                    />
                </AutoCol>
                {!!data?.length && (
                    <Col className="d-flex justify-content-end align-items-end">
                        <Button
                            className="mb-3"
                            onClick={sendPdfs}
                            disabled={!allSurgeonsHaveContactInfo || isUploading}
                        >
                            {isUploading ? "Sending..." : "Send Reports to Surgeons"}
                        </Button>
                    </Col>
                )}
            </Row>

            {invalidDates && <Alert variant="danger">Start date must be before end date.</Alert>}

            {isFetching && <LoadingSpinner />}
            {!isFetching && !data?.length && <Alert variant="info">No data found for the selected date range.</Alert>}

            {!isFetching && !!data?.length && (
                <div className="tissue-report-container">
                    <Card className="surgeons">
                        <Card.Header className="d-flex justify-content-between align-items-center">
                            Surgeons
                            <Icon
                                icon={fas.faArrowUpRightFromSquare}
                                onClick={() => setShowModal(true)}
                                className="clickable text-primary"
                                title="View surgeon contact information"
                                aria-label="View surgeon contact information"
                                tabIndex={0}
                                onKeyDown={(e) => {
                                    if (e.key === "Enter") {
                                        setShowModal(true);
                                    }
                                }}
                            />
                        </Card.Header>
                        <Card.Body>
                            <ButtonGroup vertical>
                                {data.map((group, index) => (
                                    <Button
                                        key={group.surgeon.id}
                                        variant={getSurgeonVariant(group.surgeon)}
                                        onClick={() => handleSurgeonClick(group.surgeon.id, index)}
                                        title={
                                            !surgeonHasContactInfo(group.surgeon)
                                                ? "Surgeon does not have contact information."
                                                : undefined
                                        }
                                    >
                                        {group.surgeon.firstName} {group.surgeon.lastName} ({group.tissues.length})
                                    </Button>
                                ))}
                            </ButtonGroup>
                        </Card.Body>
                    </Card>
                    <Card className="tissues">
                        <Card.Header>Tissues</Card.Header>
                        <Card.Body>
                            {data.map((group, index) => (
                                <div key={group.surgeon.id} onMouseEnter={() => setActiveSurgeonId(group.surgeon.id)}>
                                    {index > 0 && <hr />}
                                    <h4>
                                        {hasPermission(Claims.ViewPerson) ? (
                                            <Link to={`/people/${group.surgeon.id}`}>
                                                {group.surgeon.firstName} {group.surgeon.lastName}
                                            </Link>
                                        ) : (
                                            <>
                                                {group.surgeon.firstName} {group.surgeon.lastName}
                                            </>
                                        )}
                                        {!surgeonHasContactInfo(group.surgeon) && (
                                            <Badge bg="danger" className="ms-2">
                                                Missing Contact Information
                                            </Badge>
                                        )}
                                    </h4>
                                    <div
                                        className="tissue-forms"
                                        ref={(element) => {
                                            surgeonRefs.current[index] = element;
                                        }}
                                    >
                                        {group.tissues.map((tissue) => (
                                            <TissueFollowUp key={tissue.id} tissue={tissue} />
                                        ))}
                                    </div>
                                </div>
                            ))}
                        </Card.Body>
                    </Card>
                </div>
            )}

            <Modal show={showModal} onHide={() => setShowModal(false)} centered size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>Surgeon Contact Information</Modal.Title>
                </Modal.Header>
                <Modal.Body style={{ maxHeight: "70vh", overflowY: "auto" }}>
                    <Table striped bordered hover>
                        <thead>
                            <tr>
                                <th>Surgeon</th>
                                <th>Contact for Report</th>
                            </tr>
                        </thead>
                        <tbody>
                            {data?.map((group) => (
                                <tr key={group.surgeon.id}>
                                    <td>
                                        {hasPermission(Claims.ViewPerson) ? (
                                            <Link to={`/people/${group.surgeon.id}`}>
                                                {group.surgeon.firstName} {group.surgeon.lastName}
                                            </Link>
                                        ) : (
                                            <>
                                                {group.surgeon.firstName} {group.surgeon.lastName}
                                            </>
                                        )}
                                    </td>
                                    <td>{getContactInfoForSurgeon(group.surgeon)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                </Modal.Body>
            </Modal>
        </Container>
    );
}
